import React, { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import ControlledAutocomplete from '../../../../components/forms/ControlledAutocomplete';
import { AutocompleteOption } from '../../../../components/forms/types';
import { UserRoles } from '../../../../components/roster/types';
import { USERS } from '../../../../config/apiPaths';
import { PaginatedUsers } from '../../../../types/apiTypes';
import { getUserUuidHeaders } from '../../../../utils/httpUtils';
import { getUrls } from '../../../../utils/url';
import { ClassContainer } from '../../ClassContainer';

const InstructorNote = styled.div`
    font-size: 12px;
    line-height: 1.2;
    margin-top: 0.5rem;
    color: ${(props) => props.theme.colors.text.secondary};
`;

const Instructor: React.FC = () => {
    const { t } = useTranslation('create_edit_class');
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = useState<AutocompleteOption[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [inputTimer, setInputTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

    const { api } = getUrls();

    const { control, leadInstructor, setValue } = useContainer(ClassContainer);

    const userUuid = window.localStorage.getItem('user_uuid');

    useEffect(() => {
        if (leadInstructor) {
            setValue('instructor', leadInstructor.email);
        }
    }, [leadInstructor, setValue]);

    const formatUserLabel = (user) => {
        if (user.user_name && user.email) {
            return `${user.user_name} <${user.email}>`;
        }

        return user.email || '';
    };

    const getInstructors = async (search, signal): Promise<AutocompleteOption[]> => {
        const config = {
            params: {
                limit: 10,
                roles: UserRoles.Teacher,
                search: search.toLocaleLowerCase()
            },
            headers: getUserUuidHeaders(userUuid as string),
            signal
        };

        const {
            data: { data }
        } = await axios.get<PaginatedUsers>(`${api}${USERS}`, config);

        const filtered = data.filter((user) => !!user.email);

        return filtered.map((user) => ({
            label: formatUserLabel(user),
            value: user.email ?? ''
        }));
    };

    const { data, isFetching } = useQuery({
        initialData: [],
        queryKey: [USERS, searchValue],
        enabled: open,
        queryFn: async ({ signal }) => getInstructors(searchValue, signal)
    });

    useEffect(() => {
        setOptions(data);
    }, [data]);

    const onInputChange = (event, value) => {
        if (inputTimer) clearTimeout(inputTimer);

        const timer = setTimeout(() => {
            setSearchValue(value);
        }, 300);

        setInputTimer(timer);
    };

    return (
        <>
            <ControlledAutocomplete
                fullWidth
                autoHighlight
                name="instructor"
                label={t('details.instructor')}
                control={control}
                options={options}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                loading={isFetching}
                onInputChange={onInputChange}
                helperText={t('details.instructor-helper-text')}
                noOptionsText={t('details.no-options')}
                loadingText={t('details.loading-options')}
            />
            <InstructorNote>{t('details.instructor-helper-text')}</InstructorNote>
        </>
    );
};

export default Instructor;
